import { useMutation, useQueryClient } from "@tanstack/react-query";

import { workbenchIncomeVerificationApi } from "../../../../../api/client";
import { useForceExitStore } from "../../../../../stores/forceExitStore";
import { stipulationKeys } from "../../../../stipulations/api/stipulationKeys";
import { dscWorkbenchKeys } from "../../../api/dscWorkbenchKeys";
import {
  buildCallScriptSaveBody,
  type CallScriptSaveContext,
  type CallScriptSaveResponse,
} from "./callScript.savePayload";
import { callScriptKeys } from "./callScriptKeys";

/** Input for saving a completed Call Script note. */
export interface SaveCallScriptInput extends CallScriptSaveContext {
  approvalId: string;
  applicantId: string;
}

/**
 * Mutation hook for saving a completed Call Script (the modal's Save button).
 * Backed by WorkbenchIncomeVerificationApi.saveCallScript (POST), which
 * persists the call at recap-service. The request body is built by
 * `buildCallScriptSaveBody`, which sends the original call-outcome fields
 * plus the merged employment data (Verified Information overlaid onto the
 * saved Employment record — see that module's comment for the full field
 * list).
 *
 * The save response is deliberately thin (just persisted identifiers, no full
 * entry — see `CallScriptSaveResponse`), so on success:
 *   - the history query and the dashboard are invalidated to refetch from
 *     recap/the BFF rather than being patched locally — "Saved Information"
 *     is built from history (the dialed-phone row) plus the dashboard's
 *     Employment record (everything else), and the save actually persists
 *     changes to that Employment record;
 *   - the stipulations list is invalidated (a saved call can change
 *     employment/income data and trigger a stipulation recalc).
 * Both fire after every save (not just on modal close) since the dashboard
 * refresh above already happens per-save.
 * @returns {ReturnType<typeof useMutation>} The TanStack Mutation result.
 */
export function useSaveCallScript() {
  const queryClient = useQueryClient();
  const shouldForceExit = useForceExitStore((state) => state.shouldForceExit);

  return useMutation({
    mutationFn: async ({
      approvalId,
      applicantId,
      ...context
    }: SaveCallScriptInput): Promise<CallScriptSaveResponse | undefined> => {
      if (shouldForceExit) {
        return undefined;
      }
      const response = await workbenchIncomeVerificationApi.saveCallScript(
        approvalId,
        applicantId,
        buildCallScriptSaveBody(context)
      );
      return response.data as CallScriptSaveResponse;
    },
    onSuccess: (result, { approvalId, applicantId }) => {
      if (!result) {
        return;
      }
      void queryClient.invalidateQueries({ queryKey: callScriptKeys.history({ approvalId, applicantId }) });
      void queryClient.invalidateQueries({ queryKey: dscWorkbenchKeys.detail({ approvalId }) });
      void queryClient.invalidateQueries({ queryKey: stipulationKeys.lists() });
    },
  });
}
